<template>
  <div>
    <CreatorCourseCertificateViewerModal
      v-model:open="certificateViewerOpen"
      :pdf-src="certificateViewerPdfSrc"
      :download-url="certificateViewerDownloadUrl"
      title="Member Course Certificate"
    />
    <div class="mt-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-zinc-900">
          Course Progress
        </h3>
        <button
          type="button"
          class="h-10 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-sm font-semibold text-zinc-800"
          @click="loadProgress"
        >
          Refresh
        </button>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <p class="text-xs uppercase tracking-wide text-zinc-500">
            Completion
          </p>
          <p class="text-2xl font-semibold text-zinc-900 mt-1">
            {{ summary.completionPercent }}%
          </p>
        </div>
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <p class="text-xs uppercase tracking-wide text-zinc-500">
            Modules Completed
          </p>
          <p class="text-2xl font-semibold text-zinc-900 mt-1">
            {{ summary.modulesCompleted }} / {{ summary.modulesTotal }}
          </p>
        </div>
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <p class="text-xs uppercase tracking-wide text-zinc-500">
            Lessons Completed
          </p>
          <p class="text-2xl font-semibold text-zinc-900 mt-1">
            {{ summary.lessonsCompleted }} / {{ summary.lessonsTotal }}
          </p>
        </div>
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <p class="text-xs uppercase tracking-wide text-zinc-500">
            Watch Time
          </p>
          <p class="text-2xl font-semibold text-zinc-900 mt-1">
            {{ summary.watchMinutes }}m
          </p>
        </div>
        <div class="rounded-xl border border-zinc-200 bg-white p-4">
          <p class="text-xs uppercase tracking-wide text-zinc-500">
            Certificates Generated
          </p>
          <p class="text-2xl font-semibold text-zinc-900 mt-1">
            {{ summary.certificatesGenerated }}
          </p>
        </div>
      </div>

      <div class="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div class="px-4 py-3 border-b border-zinc-200 text-sm font-semibold text-zinc-800">
          Lesson Progress
        </div>

        <div
          v-if="isLoading"
          class="px-4 py-8 text-sm text-zinc-500"
        >
          Loading member course progress...
        </div>
        <div
          v-else-if="errorMessage"
          class="px-4 py-8 text-sm text-red-600"
        >
          {{ errorMessage }}
        </div>
        <div
          v-else-if="!rows.length"
          class="px-4 py-8 text-sm text-zinc-500"
        >
          No course progress recorded yet for this member.
        </div>

        <div
          v-else
          class="overflow-x-auto"
        >
          <table class="min-w-full text-sm">
            <thead class="bg-zinc-50 text-zinc-600">
              <tr>
                <th class="text-left px-4 py-2 font-medium">
                  Course
                </th>
                <th class="text-left px-4 py-2 font-medium">
                  Module
                </th>
                <th class="text-left px-4 py-2 font-medium">
                  Lesson
                </th>
                <th class="text-right px-4 py-2 font-medium">
                  Progress
                </th>
                <th class="text-left px-4 py-2 font-medium">
                  Status
                </th>
                <th class="text-left px-4 py-2 font-medium">
                  Last Activity
                </th>
                <th class="text-left px-4 py-2 font-medium">
                  Certificate
                </th>
                <th class="text-right px-4 py-2 font-medium">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="border-t border-zinc-100 hover:bg-zinc-50"
              >
                <td class="px-4 py-2 text-zinc-900">
                  {{ row.courseTitle }}
                </td>
                <td class="px-4 py-2 text-zinc-700">
                  {{ row.moduleTitle }}
                </td>
                <td class="px-4 py-2 text-zinc-700">
                  {{ row.lessonTitle }}
                </td>
                <td class="px-4 py-2 text-right text-zinc-800">
                  {{ row.progressPercent }}%
                </td>
                <td class="px-4 py-2">
                  <span
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                    :class="statusClass(row.status)"
                  >
                    {{ row.status }}
                  </span>
                </td>
                <td class="px-4 py-2 text-zinc-600">
                  {{ formatDateTime(row.lastActivityAt) }}
                </td>
                <td class="px-4 py-2 text-zinc-600">
                  <span
                    v-if="row.hasCertificate"
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-emerald-50 text-emerald-700 ring-emerald-700/20"
                  >
                    Issued
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="px-4 py-2 text-right">
                  <button
                    type="button"
                    class="px-3 py-1.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold text-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="!row.hasCertificate"
                    @click="viewCertificate(row)"
                  >
                    View Cert
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { fetchMemberCourseProgress } from '@/services/creatorEngagementService';
import CreatorCourseCertificateViewerModal from '@/Components/CreatorCourseCertificateViewerModal.vue';
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  memberId: {
    type: [String, Number],
    required: true,
  },
});

const isLoading = ref(false);
const errorMessage = ref('');
const rows = ref([]);
const summary = ref({
  completionPercent: 0,
  modulesCompleted: 0,
  modulesTotal: 0,
  lessonsCompleted: 0,
  lessonsTotal: 0,
  watchMinutes: 0,
  certificatesGenerated: 0,
});
const certificateViewerOpen = ref(false);
const certificateViewerPdfSrc = ref('');
const certificateViewerDownloadUrl = ref('');

function normalizeRow(raw, idx) {
  return {
    id: String(raw?.id || raw?.progressId || raw?.progress_id || `member-progress-${idx}`),
    featureInstanceId: Number(raw?.featureInstanceId ?? raw?.feature_instance_id ?? 0) || 0,
    courseTitle: String(raw?.courseTitle || raw?.course_title || 'Course'),
    moduleTitle: String(raw?.moduleTitle || raw?.module_title || 'Module'),
    lessonTitle: String(raw?.lessonTitle || raw?.lesson_title || 'Lesson'),
    progressPercent: Number(raw?.progressPercent ?? raw?.progress_percent ?? 0) || 0,
    status: String(raw?.status || 'In Progress'),
    lastActivityAt: raw?.lastActivityAt || raw?.last_activity_at || raw?.updatedAt || raw?.updated_at || null,
    hasCertificate: Boolean(raw?.hasCertificate ?? raw?.has_certificate),
    certificateGeneratedAt: raw?.certificateGeneratedAt || raw?.certificate_generated_at || null,
  };
}

async function loadProgress() {
  if (!props.memberId) return;
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const data = await fetchMemberCourseProgress(props.memberId, {
      limit: 300,
      offset: 0,
    });

    const items = data?.rows || data?.progress || data?.items || (Array.isArray(data) ? data : []);
    rows.value = Array.isArray(items) ? items.map((row, idx) => normalizeRow(row, idx)) : [];

    const nextSummary = data?.summary || {};
    summary.value = {
      completionPercent: Number(nextSummary?.completionPercent ?? nextSummary?.completion_percent ?? 0) || 0,
      modulesCompleted: Number(nextSummary?.modulesCompleted ?? nextSummary?.modules_completed ?? 0) || 0,
      modulesTotal: Number(nextSummary?.modulesTotal ?? nextSummary?.modules_total ?? 0) || 0,
      lessonsCompleted: Number(nextSummary?.lessonsCompleted ?? nextSummary?.lessons_completed ?? 0) || 0,
      lessonsTotal: Number(nextSummary?.lessonsTotal ?? nextSummary?.lessons_total ?? 0) || 0,
      watchMinutes: Number(nextSummary?.watchMinutes ?? nextSummary?.watch_minutes ?? 0) || 0,
      certificatesGenerated: Number(nextSummary?.certificatesGenerated ?? nextSummary?.certificates_generated ?? 0) || 0,
    };
  } catch (err) {
    errorMessage.value = err?.message || 'Failed to load member course progress';
    rows.value = [];
  } finally {
    isLoading.value = false;
  }
}

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function statusClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('complete')) return 'bg-green-50 text-green-700 ring-green-700/10';
  if (normalized.includes('locked')) return 'bg-red-50 text-red-700 ring-red-700/10';
  return 'bg-blue-50 text-blue-700 ring-blue-700/10';
}

function buildCreatorCertificateViewUrl(row = {}, { download = false } = {}) {
  const featureInstanceId = Number(row?.featureInstanceId || rows.value?.[0]?.featureInstanceId || 0);
  const memberId = Number(props.memberId || 0);
  const base = String(apiProxyUrl || '').trim();
  if (!featureInstanceId || !memberId || !base) return '';
  const params = new URLSearchParams({
    featureInstanceId: String(featureInstanceId),
  });
  if (download) params.set('download', '1');
  return `${base}/api/creator-course/certificate/member/${encodeURIComponent(String(memberId))}/pdf?${params.toString()}`;
}

function viewCertificate(row = {}) {
  const viewUrl = buildCreatorCertificateViewUrl(row, { download: false });
  const downloadUrl = buildCreatorCertificateViewUrl(row, { download: true });
  if (!viewUrl) return;
  certificateViewerPdfSrc.value = viewUrl;
  certificateViewerDownloadUrl.value = downloadUrl || viewUrl;
  certificateViewerOpen.value = true;
}

watch(
  () => props.memberId,
  () => {
    loadProgress();
  },
  { immediate: true }
);

onMounted(() => {
  loadProgress();
});
</script>
